// A royal's face. The sim deals each noble a unique portrait id; when the pool
// ran dry (`portrait: null`) or the file is missing, a crown stands in.
//
// With generations armed, `stage` picks the age file (`<id>.<stage>.webp` —
// all 68 faces exist at all four stages): the same person across a lifetime.
// The fallback chain is age file → base face → crown, so a stale asset
// directory degrades one step at a time instead of losing the face, and the
// chain resets when the stage changes (a child grows up mid-match).
import { Crown } from 'lucide-react';
import { useState } from 'react';
import { type LifeStage, portraitUrl } from '../dynasty/dynasty.js';

export function RoyalPortrait({
  portrait,
  label,
  className = 'h-20 w-16',
  thumb = true,
  stage,
}: {
  readonly portrait: string | null;
  /** Accessible name: who this is. */
  readonly label: string;
  readonly className?: string;
  readonly thumb?: boolean;
  /** The life stage the sim published, if the generations module is armed. */
  readonly stage?: LifeStage;
}) {
  // The last URL that 404'd; the candidates are tried in order past it.
  const [failed, setFailed] = useState<string | null>(null);
  const candidates: string[] = [];
  if (portrait) {
    if (stage) candidates.push(portraitUrl(portrait, thumb, stage));
    candidates.push(portraitUrl(portrait, thumb));
  }
  const url = candidates.find((u) => u !== failed) ?? null;
  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-amber-500/40 bg-muted/60 ${className}`}
    >
      {url ? (
        <img
          src={url}
          alt={label}
          loading="lazy"
          className="size-full object-cover"
          onError={() => setFailed(url)}
        />
      ) : (
        <Crown
          className="size-6 text-amber-600/80 dark:text-amber-200/70"
          role="img"
          aria-label={label}
        />
      )}
    </span>
  );
}
